import { useUrlParam } from "@opendash/core";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { Collapse, Empty } from "antd";
import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";
import { SourceMetaItem } from "./SourceMetaItem";
import { SourceMetaLabel } from "./SourceMetaLabel";

type Props = {
  sources: Source[]; // the sources that can be filtered by the search string
};

export const SourceMetaViewer = observer((props: Props) => {
  const [searchString] = useUrlParam<string>("search", "", "string");

  const filteredSources = useMemo(() => {
    if (!searchString) return props.sources;

    return props.sources.filter((source) => {
      let found = true;
      const metaString = getMetaValuesString(source);
      const splittedSearchParams = searchString.split(",").map((s) => s.trim());

      splittedSearchParams.forEach((searchValue) => {
        if (!metaString.toLowerCase().includes(searchValue.toLowerCase())) {
          found = false;
        }
      });

      return found;
    });
  }, [searchString, props.sources]);

  return (
    <>
      {filteredSources.length > 0 && (
        <Collapse
          items={filteredSources.map((source) => {
            return {
              label: <SourceMetaLabel source={source} />,
              children: <SourceMetaItem source={source} />,
            };
          })}
        />
      )}
      {filteredSources.length === 0 && <Empty />}
    </>
  );
});

/**
 * Get the meta values of a source as a string to compare with the search string
 * @param source the source you want to get the meta values from
 * @returns the meta values as a string separated by commas
 */
export function getMetaValuesString(source: Source) {
  if (!source) return "";
  const metaValues: string[] = [];
  metaValues.push(source.name);
  if (source.meta?.data) {
    const metaKeys = Object.keys(source.meta.data);
    for (const index in metaKeys) {
      const metaKey = metaKeys[index];
      const metaValue = source.meta.data[metaKey];
      let pushValue = metaValue;
      if (metaValue instanceof Array) {
        pushValue = metaValue.join(", ");
      }
      metaValues.push(pushValue);
    }
  }
  return metaValues.join(", ");
}
